// The fractal flame on the CPU - the same chaos game as visualizers/flame.js's
// shader, for the two things the GPU cannot tell us cheaply: WHERE the
// attractor is (the automatic framing, run every beat on a few thousand
// points), and whether a preset COLLAPSES under its rotation
// (tools/validate-flames.mjs, offline). No three.js here: the validator runs
// it in node.
import PRESET_FILE from './flame-presets.json' with { type: 'json' };

export const VARIATIONS = ['linear', 'sinusoidal', 'spherical', 'swirl', 'horseshoe', 'julia'];
export const DET_MIN = 0.25, DET_MAX = 0.85;   // |det| of an affine's linear part
export const FRAME_W = 0.90, FRAME_H = 0.95;   // of the width, of the height
export const COVER_GRID = [64, 36];
export const COLLAPSE = 0.30, RECOVER = 0.40;  // coverage: reverse below, resume above
export const SAFE_SHARE = 0.25;                // of the points behind the cover: open around it

// The presets with every affine's |det| held in [DET_MIN, DET_MAX]: below,
// the attractor gathers into a point; above, it fills the plane as fog.
export function normalizedPresets() {
  return PRESET_FILE.presets.map(function (p) {
    const t = p.t.map(function (r) {
      const det = Math.abs(r[1] * r[5] - r[2] * r[4]);
      const target = Math.min(DET_MAX, Math.max(DET_MIN, det));
      const k = det > 1e-9 ? Math.sqrt(target / det) : 1;
      return [r[0], r[1] * k, r[2] * k, r[3], r[4] * k, r[5] * k, r[6], r[7]];
    });
    return Object.assign({}, p, { t: t });
  });
}

// The coefficients of the moment: transform k's angle th turns its OFFSET
// (by th itself when orbit >= pi, else by orbit x sin th) and swings its
// linear part by swing x sin th. Returns 4 rows [a, b, c, d, e, f].
export function coefficients(preset, angles, out) {
  const T = preset.t, o = out || [[], [], [], []];
  const swing = preset.swing != null ? preset.swing : 0.35;
  const orbit = preset.orbit != null ? preset.orbit : Math.PI;
  for (let k = 0; k < 4; k++) {
    const t = T[k] || T[T.length - 1];
    const th = angles[k];
    const sw = swing * Math.sin(th);
    const cs = Math.cos(sw), ss = Math.sin(sw);
    const oa = orbit >= Math.PI - 1e-3 ? th : orbit * Math.sin(th);
    const co = Math.cos(oa), so = Math.sin(oa);
    o[k][0] = cs * t[1] - ss * t[4]; o[k][1] = cs * t[2] - ss * t[5]; o[k][2] = co * t[3] - so * t[6];
    o[k][3] = ss * t[1] + cs * t[4]; o[k][4] = ss * t[2] + cs * t[5]; o[k][5] = so * t[3] + co * t[6];
  }
  return o;
}

function variation(v, x, y, om, out) {
  const r2 = Math.max(x * x + y * y, 1e-6), r = Math.sqrt(r2);
  if (v === 0) { out[0] = x; out[1] = y; }
  else if (v === 1) { out[0] = Math.sin(x); out[1] = Math.sin(y); }
  else if (v === 2) { out[0] = x / r2; out[1] = y / r2; }
  else if (v === 3) { const s = Math.sin(r2), c = Math.cos(r2); out[0] = x * s - y * c; out[1] = x * c + y * s; }
  else if (v === 4) { out[0] = (x - y) * (x + y) / r; out[1] = 2 * x * y / r; }
  else { const th = Math.atan2(x, y) * 0.5 + om, sr = Math.sqrt(r); out[0] = sr * Math.cos(th); out[1] = sr * Math.sin(th); }
}

function mulberry32(a) {
  return function () { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}

// The chaos game: n points, iter steps each, from a FIXED seed so the
// measured bounds do not jitter from one run to the next. varA/varB/varMix:
// the variation per transform and the phrase's fade (as in the shader).
export function chaos(preset, coeffs, n, iter, varA, varB, varMix) {
  const T = preset.t, rnd = mulberry32(12345);
  let w = 0; for (const t of T) w += t[0];
  const cum = []; let c = 0; for (const t of T) { c += t[0] / w; cum.push(c); }
  cum[cum.length - 1] = 1.01;
  const xs = new Float32Array(n), ys = new Float32Array(n), ok = new Uint8Array(n);
  const tmp = [0, 0], tmp2 = [0, 0];
  for (let i = 0; i < n; i++) {
    let x = (rnd() * 2 - 1) * 0.05, y = (rnd() * 2 - 1) * 0.05;
    for (let it = 0; it < iter; it++) {
      const u = rnd(), om = rnd() < 0.5 ? 0 : Math.PI;
      let k = 0; while (k < cum.length - 1 && u >= cum[k]) k++;
      const a = coeffs[k];
      const qx = a[0] * x + a[1] * y + a[2], qy = a[3] * x + a[4] * y + a[5];
      const va = varA ? varA[k] : T[k][7];
      variation(va, qx, qy, om, tmp);
      if (varMix && varMix[k] > 0.001) { variation(varB[k], qx, qy, om, tmp2); tmp[0] += (tmp2[0] - tmp[0]) * varMix[k]; tmp[1] += (tmp2[1] - tmp[1]) * varMix[k]; }
      x = Math.max(-1e3, Math.min(1e3, tmp[0])); y = Math.max(-1e3, Math.min(1e3, tmp[1]));
    }
    xs[i] = x; ys[i] = y; ok[i] = (Math.abs(x) < 50 && Math.abs(y) < 50) ? 1 : 0;
  }
  return { xs, ys, ok };
}

// The same game, a slice at a time: start() then step(count) until done.
// On the Pi a run of 8 000 points costs 54 ms of JavaScript; spread over 16
// frames it is about 3 ms a frame.
export function chaosJob(preset, coeffs, n, iter, varA, varB, varMix) {
  const T = preset.t, rnd = mulberry32(12345);
  let w = 0; for (const t of T) w += t[0];
  const cum = []; let c = 0; for (const t of T) { c += t[0] / w; cum.push(c); }
  cum[cum.length - 1] = 1.01;
  const co = coeffs.map((r) => r.slice());
  const va = varA ? varA.slice() : T.map((t) => t[7]), vb = varB ? varB.slice() : va, vm = varMix ? varMix.slice() : [0, 0, 0, 0];
  const xs = new Float32Array(n), ys = new Float32Array(n), ok = new Uint8Array(n);
  const tmp = [0, 0], tmp2 = [0, 0];
  let i = 0;
  return {
    get done() { return i >= n; },
    result() { return { xs, ys, ok }; },
    step(count) {
      const end = Math.min(n, i + count);
      for (; i < end; i++) {
        let x = (rnd() * 2 - 1) * 0.05, y = (rnd() * 2 - 1) * 0.05;
        for (let it = 0; it < iter; it++) {
          const u = rnd(), om = rnd() < 0.5 ? 0 : Math.PI;
          let k = 0; while (k < cum.length - 1 && u >= cum[k]) k++;
          const a = co[k];
          const qx = a[0] * x + a[1] * y + a[2], qy = a[3] * x + a[4] * y + a[5];
          variation(va[k], qx, qy, om, tmp);
          if (vm[k] > 0.001) { variation(vb[k], qx, qy, om, tmp2); tmp[0] += (tmp2[0] - tmp[0]) * vm[k]; tmp[1] += (tmp2[1] - tmp[1]) * vm[k]; }
          x = Math.max(-1e3, Math.min(1e3, tmp[0])); y = Math.max(-1e3, Math.min(1e3, tmp[1]));
        }
        xs[i] = x; ys[i] = y; ok[i] = (Math.abs(x) < 50 && Math.abs(y) < 50) ? 1 : 0;
      }
    }
  };
}

function quantile(arr, q) {
  const a = Array.from(arr).sort(function (u, v) { return u - v; });
  return a.length ? a[Math.min(a.length - 1, Math.max(0, Math.floor(q * (a.length - 1))))] : 0;
}

// THE FRAMING. The points (in flame space) turned by the global angle; the
// 2nd-98th percentile box brought to FRAME_W of the width (height capped at
// FRAME_H), centred. Units: the frame's height is 2, its width 2 x aspect
// (the shader divides x by the aspect). Coverage: the share of a 64 x 36 grid
// the framed points touch. inSafe: the share of the points behind the cover;
// above SAFE_SHARE, `wings` frames the shape into the band LEFT of the cover
// (the visualizer then mirrors it, and it opens around the cover).
export function frame(pts, globalAngle, aspect, safe) {
  const cr = Math.cos(globalAngle), sr = Math.sin(globalAngle);
  const X = [], Y = [];
  for (let i = 0; i < pts.xs.length; i++) {
    if (!pts.ok[i]) continue;
    const x = pts.xs[i], y = pts.ys[i];
    X.push(x * cr - y * sr); Y.push(x * sr + y * cr);
  }
  if (X.length < 10) return null;
  const x2 = quantile(X, 0.02), x98 = quantile(X, 0.98), y2 = quantile(Y, 0.02), y98 = quantile(Y, 0.98);
  const w = Math.max(1e-4, x98 - x2), h = Math.max(1e-4, y98 - y2);
  const mx = (x2 + x98) / 2, my = (y2 + y98) / 2;
  let scale = Math.min(FRAME_W * 2 * aspect / w, FRAME_H * 2 / h);
  let ox = -mx * scale, oy = -my * scale;
  // coverage and the share behind the cover, at that framing
  const [gw, gh] = COVER_GRID, cells = new Uint8Array(gw * gh);
  let inSafe = 0, sumX = 0, sumY = 0;
  for (let i = 0; i < X.length; i++) {
    const sx = X[i] * scale + ox, sy = Y[i] * scale + oy;
    sumX += sx; sumY += sy;
    const cx = Math.floor((sx / aspect * 0.5 + 0.5) * gw), cy = Math.floor((sy * 0.5 + 0.5) * gh);
    if (cx >= 0 && cx < gw && cy >= 0 && cy < gh) cells[cy * gw + cx] = 1;
    if (safe && Math.abs(sx - safe.cx) < safe.hw && Math.abs(sy - safe.cy) < safe.hh) inSafe++;
  }
  let covered = 0; for (let i = 0; i < cells.length; i++) covered += cells[i];
  const out = {
    scale, ox, oy, coverage: covered / cells.length, inSafe: inSafe / X.length, wings: false,
    bounds: [x2, x98, y2, y98], mass: [sumX / X.length, sumY / X.length]
  };
  if (safe && out.inSafe > SAFE_SHARE) {
    // the band left of the cover
    const xl = -aspect * FRAME_W, xr = safe.cx - safe.hw - 0.04;
    const bw = Math.max(0.2, xr - xl);
    const s2 = Math.min(0.95 * bw / w, FRAME_H * 2 / h);
    out.scale = s2; out.ox = (xl + xr) / 2 - mx * s2; out.oy = -my * s2; out.wings = true;
  }
  return out;
}

// The angles after `beats` of rotation, as the visualizer turns them at a
// neutral mid band (speed 1): transform k turns one TURN_BEATS turn, sign
// alternating, a little faster with k.
export const TURN_BEATS = 32;
export function anglesAt(beats, dir) {
  const a = [0, 0, 0, 0];
  for (let k = 0; k < 4; k++) a[k] = (beats / TURN_BEATS) * Math.PI * 2 * (k % 2 ? -1 : 1) * (1 + 0.15 * k) * (dir || 1);
  return a;
}
